import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type RootState from 'app-components/utils/redux-store/rootState';

import { closeChat } from './aiChatActions';
import type { CachedQueryEntry } from './tools/aiQueryTypes';

export const AI_QUERY_CACHE_REDUCER_KEY = 'aiQueryCache';

type AiQueryCacheState = {
  entries: Record<string, CachedQueryEntry>; // keyed by resultId
  index: Record<string, string>; // queryKey → resultId
};

const initialState: AiQueryCacheState = {
  entries: {},
  index: {},
};

const aiQueryCacheSlice = createSlice({
  name: AI_QUERY_CACHE_REDUCER_KEY,
  initialState,
  reducers: {
    cacheQueryResult(state, action: PayloadAction<CachedQueryEntry>) {
      const entry = action.payload;
      state.entries[entry.resultId] = entry;
      if (entry.queryKey) {
        state.index[entry.queryKey] = entry.resultId;
      }
    },
    clearConversationCache(state, action: PayloadAction<string>) {
      const conversationId = action.payload;
      for (const resultId of Object.keys(state.entries)) {
        const entry = state.entries[resultId];
        if (entry.conversationId === conversationId) {
          if (entry.queryKey) delete state.index[entry.queryKey];
          delete state.entries[resultId];
        }
      }
    },
  },
  extraReducers(builder) {
    // Clear all cache when the chat closes to prevent stale financial data
    // from being referenced across sessions.
    builder.addCase(closeChat, (state) => {
      state.entries = {};
      state.index = {};
    });
  },
});

export const { cacheQueryResult, clearConversationCache } = aiQueryCacheSlice.actions;
export default aiQueryCacheSlice.reducer;

export const aiQueryCacheSelectors = {
  getEntry: (state: RootState, resultId: string): CachedQueryEntry | undefined =>
    state[AI_QUERY_CACHE_REDUCER_KEY]?.entries?.[resultId],
  getEntryByQueryKey: (state: RootState, queryKey: string): CachedQueryEntry | undefined => {
    const resultId = state[AI_QUERY_CACHE_REDUCER_KEY]?.index?.[queryKey];
    return resultId ? state[AI_QUERY_CACHE_REDUCER_KEY]?.entries?.[resultId] : undefined;
  },
  getEntriesForConversation: (state: RootState, conversationId: string): CachedQueryEntry[] =>
    Object.values(state[AI_QUERY_CACHE_REDUCER_KEY]?.entries ?? {}).filter(
      (entry) => entry.conversationId === conversationId,
    ),
};

declare module 'app-components/utils/redux-store/rootState' {
  export default interface RootState {
    [AI_QUERY_CACHE_REDUCER_KEY]: AiQueryCacheState;
  }
}
